/**
 * Normalize a date string to zero-padded ISO YYYY-MM-DD.
 * Accepts: YYYY-MM-DD, YYYY-M-D, MM/DD/YYYY, M/D/YYYY.
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export function normalizeYmdDate(value) {
  if (value == null || value === '') return null;
  const v = String(value).trim();
  let year;
  let month;
  let day;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(v);
  if (iso) {
    year = parseInt(iso[1], 10);
    month = parseInt(iso[2], 10);
    day = parseInt(iso[3], 10);
  } else {
    const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
    if (!slash) return null;
    month = parseInt(slash[1], 10);
    day = parseInt(slash[2], 10);
    year = parseInt(slash[3], 10);
  }
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const m = month < 10 ? `0${month}` : `${month}`;
  const d = day < 10 ? `0${day}` : `${day}`;
  return `${year}-${m}-${d}`;
}

/**
 * Format a date value for display as MM/DD/YYYY.
 * Accepts: YYYY-MM-DD string, Date, or Firestore Timestamp.
 * @param {string|Date|{ toDate: () => Date }} val
 * @returns {string} MM/DD/YYYY or '—' if missing/invalid
 */
export function formatDateDisplay(val) {
  if (!val) return '—';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val.trim())) {
    return formatDateDisplayFromString(val);
  }
  const d = val.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${y}`;
}

/**
 * Format a YYYY-MM-DD string for display as MM/DD/YYYY.
 * @param {string} yyyyMmDd
 * @returns {string} MM/DD/YYYY or '—' if missing/invalid
 */
export function formatDateDisplayFromString(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return '—';
  const s = yyyyMmDd.trim().slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s || '—';
  return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
}

/** Today's calendar date in local time as YYYY-MM-DD. */
export function todayYmdLocal() {
  return new Date().toLocaleDateString('en-CA');
}

/**
 * Convert Date / Firestore Timestamp / YYYY-MM-DD string to YYYY-MM-DD (local calendar day).
 * @param {string|Date|{ toDate: () => Date }|null|undefined} val
 * @returns {string}
 */
export function toYmdLocal(val) {
  if (!val) return '';
  if (typeof val === 'string') {
    const s = val.trim().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
  }
  const d = val.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse YYYY-MM-DD as a local midnight Date (for Firestore Timestamp writes).
 * @param {string} ymd
 * @returns {Date|null}
 */
export function ymdToLocalDate(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const s = ymd.trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}
